import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { APP_POOL_CONFIG } from '@synergy/config';
import { Dispatch } from 'redux';
import { PaginatedResponse } from '@synergy/api';

import { Api } from '../../../api';
import { AppState } from '../../../store/models/app-state';
import { Employee } from '../models/employee';
import { EmployeeRequest } from '../models/employee-request';
import { ErrorMessage } from '../../../common/api/models/error-message';

export const CREATE_EMPLOYEE_COMPLETED = 'CREATE_EMPLOYEE_COMPLETED';
export const UPDATE_EMPLOYEE_COMPLETED = 'UPDATE_EMPLOYEE_COMPLETED';
export const RECEIVE_EMPLOYEES = 'RECEIVE_EMPLOYEES';
export const REQUEST_EMPLOYEES = 'REQUEST_EMPLOYEES';
export const RESET_USER = 'RESET_USER';
export const START_CREATE_EMPLOYEE = 'START_CREATE_EMPLOYEE';
export const START_UPDATE_EMPLOYEE = 'START_UPDATE_EMPLOYEE';
export const EMAIL_EXISTS_WARNING = 'EMAIL_EXISTS_WARNING';
export const DELETE_EMPLOYEE = 'DELETE_EMPLOYEE';
export const DELETE_EMPLOYEE_SUCCESS = 'DELETE_EMPLOYEE_SUCCESS';
export const DELETE_EMPLOYEE_FAILED = 'DELETE_EMPLOYEE_FAILED';
export const DELETE_EMPLOYEE_CONDITION_ERROR = 'DELETE_EMPLOYEE_CONDITION_ERROR';
export const DELETE_USER = 'DELETE_USER';

export const UPDATE_MAIN_CONTACT = 'UPDATE_MAIN_CONTACT';
export const UPDATE_IMPORT_CONTACT = 'UPDATE_IMPORT_CONTACT';

const hasContactAssignedError = (title: string | undefined) => {
    return (
        title === ErrorMessage.ContactIsAssignedAsMainContact ||
        title === ErrorMessage.ContactIsAssignedToBonusCondition
    );
};

const requestContactUsers = () => {
    return { type: REQUEST_EMPLOYEES };
};

const receiveContactUsers = (paginatedUsers: PaginatedResponse<Employee>) => {
    return { type: RECEIVE_EMPLOYEES, paginatedUsers };
};

const startPatch = () => {
    return { type: START_UPDATE_EMPLOYEE };
};

const patchCompleted = () => {
    return { type: UPDATE_EMPLOYEE_COMPLETED };
};

const startCreate = () => {
    return { type: START_CREATE_EMPLOYEE };
};

const createCompleted = (user: Employee) => {
    return { type: CREATE_EMPLOYEE_COMPLETED, user };
};

export const userExistsError = () => {
    return { type: EMAIL_EXISTS_WARNING };
};

export const createEmployee = (newUser: EmployeeRequest) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startCreate());
        const response = await api.userManagement.createEmployee(newUser);
        if (response.success) {
            dispatch(createCompleted(response.result));
        } else {
            if (response.error?.title === ErrorMessage.NonUnique) {
                dispatch(userExistsError());
                return false;
            }
        }
        return true;
    };
};

export const patchUser = (user: EmployeeRequest) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const response = await api.userManagement.updateEmployee(user);
        if (response.success) {
            dispatch(patchCompleted());
        } else {
            if (response.error?.title === ErrorMessage.NonUnique) {
                dispatch(userExistsError());
                return false;
            }
        }
        return true;
    };
};

export const resetUser = () => {
    return { type: RESET_USER };
};

export const fetchEmployees = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerAccountId = getState().currentAccount?.id;
        if (!customerAccountId) {
            return;
        }

        dispatch(requestContactUsers());

        const response = await api.userManagement.cget(customerAccountId, searchParams);

        if (response.success) {
            dispatch(receiveContactUsers(response.result));
        }
    };
};

export const updateMainContact = (mainContactId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: UPDATE_MAIN_CONTACT, mainContactId });
        await api.userManagement.updateMainContact(mainContactId);
    };
};

export const updateImportContact = (importContactId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: UPDATE_IMPORT_CONTACT, importContactId });
        await api.userManagement.updateImportContact(importContactId);
    };
};

export const deleteLoginUser = (userId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DELETE_USER, userId });
        await api.userManagement.deleteLoginUser(userId);
    };
};

export const deleteEmployee = (
    userId: number,
    reassignMainContactId?: number,
    reassignBonusContactId?: number,
    reassignImportContactId?: number
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DELETE_EMPLOYEE, userId });
        const response = await api.userManagement.removeEmployee(
            userId,
            reassignMainContactId,
            reassignBonusContactId,
            reassignImportContactId
        );

        if (response.success) {
            dispatch({
                type: DELETE_EMPLOYEE_SUCCESS,
                userId,
                reassignMainContactId,
                reassignBonusContactId,
                reassignImportContactId,
            });
            dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.deleteEmployeeSuccess')));
        } else if (hasContactAssignedError(response.error?.title)) {
            dispatch({ type: DELETE_EMPLOYEE_CONDITION_ERROR });
            dispatch(
                Snackbar.enqueue(
                    Snackbar.PoolNotification.error('app.snackbar.deleteEmployeeFailed', {
                        poolConfigNameShort: APP_POOL_CONFIG['name_short'],
                    })
                )
            );
        } else {
            dispatch({ type: DELETE_EMPLOYEE_FAILED });
            dispatch(
                Snackbar.enqueue(
                    Snackbar.PoolNotification.error('app.snackbar.deleteEmployeeFailed', {
                        poolConfigNameShort: APP_POOL_CONFIG['name_short'],
                    })
                )
            );
        }
    };
};
